/**
 * UpSurv 2.0 — runtime schema for the survey JSON contract.
 *
 * This file is the runtime twin of `types.ts`. types.ts is the contract at
 * compile time; schema.ts is the same contract enforced at every wire boundary
 * the document crosses — IndexedDB, background sync, D1, and eventually the
 * estimate engine. Drift between the two is a bug, so the bottom of this file
 * carries compile-time proofs that every Zod schema infers EXACTLY its
 * counterpart interface. Change one without the other and the build stops.
 *
 * Scope discipline — what this file does NOT do:
 *   Document invariants live here (shape, id uniqueness, canonical key format,
 *   schema-version compatibility). The business rules of specification §4 —
 *   disconnect sums, ladder ceilings, the shunt refurb→replace flip — belong to
 *   the validation engine. A survey whose mainline counts don't add up is a
 *   *valid document carrying an invalid answer*: it must still parse, sync, and
 *   round-trip, or a surveyor loses a day's work to a failed submit.
 *
 * Unknown keys are stripped, not rejected. A document written by a future 2.x
 * build must still be readable by this one (see `assertSchemaVersion`), and
 * `.strict()` would reject it outright while `.passthrough()` would smuggle an
 * index signature into the inferred types and break the drift guards below.
 */

import { z } from 'zod';
import { zodToJsonSchema } from 'zod-to-json-schema';

import {
  SCHEMA_VERSION,
  type AdHocItems,
  type AdHocOther,
  type AnswerKind,
  type AnswerValue,
  type AutoFix,
  type BuildingInfo,
  type CarRef,
  type Contact,
  type DerivedLine,
  type EntityLevel,
  type FlagCount,
  type FormSet,
  type Gap,
  type GapAcknowledgement,
  type GapReason,
  type Group,
  type Hoistway,
  type IssueTally,
  type MachineRoom,
  type Measure,
  type Note,
  type NotificationEvent,
  type NotificationPayload,
  type PhotoRef,
  type PhotoUploadState,
  type PricingMode,
  type RoomType,
  type Scope,
  type SectionKind,
  type SectionStatus,
  type SubmittedSurvey,
  type SurveyDocument,
  type SurveyOrigin,
  type SurveyStatus,
  type ValidationIssue,
  type ValidationSeverity,
  type WallRange,
  type WallType,
} from './types';

import type { FlsCutSheet } from './flsCutSheet';
import type { VendorForm } from './flsVendorForm';

export { SCHEMA_VERSION };

/* ════════════════════════════ versioning ════════════════════════════ */

export interface SemVer {
  major: number;
  minor: number;
  patch: number;
}

const SEMVER_RE = /^(\d+)\.(\d+)\.(\d+)$/;

export function parseSemVer(raw: string): SemVer | null {
  const m = SEMVER_RE.exec(raw);
  if (!m) return null;
  // noUncheckedIndexedAccess treats capture groups as possibly-undefined.
  const [, major, minor, patch] = m;
  if (major === undefined || minor === undefined || patch === undefined) return null;
  return { major: Number(major), minor: Number(minor), patch: Number(patch) };
}

/** The version this build speaks, parsed once from types.ts. */
export const CURRENT_SCHEMA_VERSION: SemVer = (() => {
  const parsed = parseSemVer(SCHEMA_VERSION);
  if (!parsed) {
    throw new Error(`SCHEMA_VERSION "${SCHEMA_VERSION}" in types.ts is not a semver string`);
  }
  return parsed;
})();

/**
 * Compatibility verdicts, at MAJOR/MINOR granularity — patch differences are
 * never meaningful to a reader.
 *
 *   current            same major, same minor
 *   compatible_older   same major, older minor — we know every field it has
 *   compatible_newer   same major, newer minor — additive-only by policy, so
 *                      unknown fields are stripped and the rest is read
 *   too_old / too_new  different major — refuse, loudly
 */
export type SchemaVersionVerdict =
  | 'current'
  | 'compatible_older'
  | 'compatible_newer'
  | 'too_old'
  | 'too_new'
  | 'malformed';

export function schemaVersionVerdict(raw: unknown): SchemaVersionVerdict {
  if (typeof raw !== 'string') return 'malformed';
  const v = parseSemVer(raw);
  if (!v) return 'malformed';
  if (v.major > CURRENT_SCHEMA_VERSION.major) return 'too_new';
  if (v.major < CURRENT_SCHEMA_VERSION.major) return 'too_old';
  if (v.minor > CURRENT_SCHEMA_VERSION.minor) return 'compatible_newer';
  if (v.minor < CURRENT_SCHEMA_VERSION.minor) return 'compatible_older';
  return 'current';
}

export function isSupportedSchemaVersion(raw: unknown): boolean {
  const verdict = schemaVersionVerdict(raw);
  return verdict === 'current' || verdict === 'compatible_older' || verdict === 'compatible_newer';
}

function versionMessage(verdict: SchemaVersionVerdict, raw: unknown): string {
  const got = typeof raw === 'string' ? `"${raw}"` : JSON.stringify(raw) ?? 'undefined';
  switch (verdict) {
    case 'too_new':
      return (
        `Survey document declares schemaVersion ${got}, a newer major version than this ` +
        `build understands (${SCHEMA_VERSION}). Refusing to read it rather than guess at ` +
        `fields it does not know about — update the app.`
      );
    case 'too_old':
      return (
        `Survey document declares schemaVersion ${got}, which predates the UpSurv 2.0 ` +
        `contract (${SCHEMA_VERSION}). There is no migration path: 2.0 is a rebuild, not ` +
        `an upgrade.`
      );
    default:
      return (
        `Survey document has no usable schemaVersion (got ${got}). Expected a semver ` +
        `string such as "${SCHEMA_VERSION}".`
      );
  }
}

export class SchemaVersionError extends Error {
  readonly documentVersion: unknown;
  readonly supportedVersion: string = SCHEMA_VERSION;
  readonly verdict: SchemaVersionVerdict;

  constructor(verdict: SchemaVersionVerdict, documentVersion: unknown) {
    super(versionMessage(verdict, documentVersion));
    this.name = 'SchemaVersionError';
    this.verdict = verdict;
    this.documentVersion = documentVersion;
  }
}

/**
 * The version gate. Accepts anything in the current major; throws a
 * `SchemaVersionError` on a future major, a past major, or a missing/garbled
 * version string. Called before field validation so a version mismatch reads as
 * one clear sentence instead of fifty field errors.
 */
export function assertSchemaVersion(doc: { schemaVersion?: unknown } | null | undefined): SemVer {
  const raw = doc?.schemaVersion;
  const verdict = schemaVersionVerdict(raw);
  const parsed = typeof raw === 'string' ? parseSemVer(raw) : null;
  if (!parsed || !isSupportedSchemaVersion(raw)) {
    throw new SchemaVersionError(verdict, raw);
  }
  return parsed;
}

/* ════════════════════════════ enumerations ════════════════════════════ */

export const ROOM_TYPES = ['traction', 'hydro', 'mrl'] as const;
export const RoomTypeSchema = z.enum(ROOM_TYPES);

export const FORM_SETS = ['traction', 'hydro'] as const;
export const FormSetSchema = z.enum(FORM_SETS);

export const SURVEY_ORIGINS = ['assigned', 'field_initiated'] as const;
export const SurveyOriginSchema = z.enum(SURVEY_ORIGINS);

export const SURVEY_STATUSES = [
  'assigned',
  'in_progress',
  'submitted_clean',
  'submitted_with_gaps',
] as const;
export const SurveyStatusSchema = z.enum(SURVEY_STATUSES);

export const PRICING_MODES = ['AUTO', 'SURV-QTY', 'FLAT', 'INFO', 'OFFICE'] as const;
export const PricingModeSchema = z.enum(PRICING_MODES);

export const ENTITY_LEVELS = ['building', 'room', 'group', 'hoistway', 'survey'] as const;
export const EntityLevelSchema = z.enum(ENTITY_LEVELS);

export const SECTION_KINDS = [
  'building',
  'room_setup',
  'electrical',
  'hvac',
  'general',
  'overhead',
  'pit',
  'lobby',
  'cab',
  'access',
  'fls',
  'adhoc',
] as const;
export const SectionKindSchema = z.enum(SECTION_KINDS);

export const SECTION_STATUSES = ['todo', 'in_progress', 'done', 'flagged'] as const;
export const SectionStatusSchema = z.enum(SECTION_STATUSES);

/** Amendment 5 added `stucco`; the L2 option list and `WallType` agree with it. */
export const WALL_TYPES = [
  'sheetrock',
  'block',
  'brick',
  'cement',
  'tile',
  'marble',
  'stucco',
] as const;
export const WallTypeSchema = z.enum(WALL_TYPES);

export const PHOTO_UPLOAD_STATES = ['pending', 'uploading', 'uploaded', 'failed'] as const;
export const PhotoUploadStateSchema = z.enum(PHOTO_UPLOAD_STATES);

export const GAP_REASONS = [
  'unanswered',
  'validation_failed',
  'missing_photo',
  'missing_required_photo',
  'surveyor_flagged',
] as const;
export const GapReasonSchema = z.enum(GAP_REASONS);

export const VALIDATION_SEVERITIES = ['error', 'warning', 'info'] as const;
export const ValidationSeveritySchema = z.enum(VALIDATION_SEVERITIES);

export const NOTIFICATION_EVENTS = [
  'survey_created_field',
  'survey_submitted',
  // D-018. Fires when the office sends a submitted survey back to the surveyor;
  // the only event that can fire more than once for one survey.
  'survey_recalled',
] as const;
export const NotificationEventSchema = z.enum(NOTIFICATION_EVENTS);

export const ANSWER_KINDS = [
  'text',
  'number',
  'bool',
  'tristate',
  'select',
  'multiselect',
  'contact',
  'date',
  'measure',
  'ranges',
  'flagCount',
] as const;
export const AnswerKindSchema = z.enum(ANSWER_KINDS);

/* ══════════════════════════ primitive shapes ══════════════════════════ */

const Id = z.string().min(1);

/** ISO-8601 instant. `offset: true` so a device that stamps -05:00 still parses. */
const Timestamp = z.string().datetime({ offset: true });

/**
 * Calendar date, not an instant. B10 (bid due date) is the only `date` question
 * in specification §3 and a due date has no time-of-day.
 */
const CalendarDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');

const Count = z.number().int().nonnegative();

/**
 * Canonical answer key produced by `answerKey()`: optional scope segments in
 * room → group → hoistway order, then the specification §3 question ID.
 * Enforced because question IDs are the estimate engine's stable keys and a
 * hand-assembled key in the wrong segment order would silently orphan an answer.
 *
 * The question-ID grammar is letter(s) + number + optional lowercase letter
 * (B17, E1a, L9c, P9b) plus ONE optional trailing digit. That last digit exists
 * for exactly one id: `E4c1`, the shunt follow-up the specification describes
 * inside the E4c row but never names (see catalog.ts). Without it a real answer
 * key fails validation and the §4 rule 5 auto-fix has nowhere to store its
 * trigger.
 */
export const AnswerKeySchema = z
  .string()
  .regex(
    /^(?:room:[^|:]+\|)?(?:grp:[^|:]+\|)?(?:hw:[^|:]+\|)?[A-Z]{1,2}\d{1,3}[a-z]?\d?$/,
    'expected a canonical answer key, e.g. "room:mr-1|hw:hw-2|P9a"',
  );

/** Canonical section instance ID produced by `sectionInstanceId()`. */
export const SectionInstanceIdSchema = z
  .string()
  .regex(
    new RegExp(
      `^(?:room:[^|:]+\\|)?(?:grp:[^|:]+\\|)?(?:hw:[^|:]+\\|)?(?:${SECTION_KINDS.join('|')})$`,
    ),
    'expected a canonical section instance id, e.g. "room:mr-1|hw:hw-2|pit"',
  );

/**
 * What a PHOTO may name as the thing it is attached to.
 *
 * ── Why this is not `AnswerKeySchema`, and not §3 question grammar either ────
 *
 * A photo does not only hang off a specification §3 question. It also hangs off
 * an Ad Hoc "Something else" item (spec §2.5), whose id is minted by
 * `adHocPhotoQuestionId()` as `adhoc.other.adhoc-<uuid>` — a real, permanent
 * attachment point that is not a question and will never match `[A-Z]\d…`.
 *
 * THE 2026-08-06 FIELD INCIDENT IS EXACTLY THIS SCHEMA NOT EXISTING. The
 * document boundary took `z.string().min(1)` and the photo-upload boundary took
 * the §3 question grammar, so a surveyor could attach a photo to an ad hoc item,
 * watch the document sync (PUT 201), and watch that one photo be refused 400 on
 * every drain forever. Two boundaries describing the same field are one bug
 * waiting for a Tuesday; there is now one schema and both import it.
 *
 * The constraint that actually matters here is not grammar but SAFETY: this
 * value becomes a path segment inside an R2 object key (`photoR2Key`), which
 * does not sanitize it. So the rule is the same one `SAFE_ID` states in the API
 * — no slash, no `..`, no separator used by `answerKey()` or
 * `sectionInstanceId()` — and any future synthetic attachment id that obeys it
 * is accepted by both sides on the day it is invented.
 */
export const PhotoQuestionIdSchema = z
  .string()
  .regex(
    /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/,
    'expected a question id (E1a) or an ad hoc item id (adhoc.other.…) — ' +
      'letters, digits, dot, dash and underscore only',
  );

/**
 * A shot list id, on a `Note`, a `PhotoRef`, a `CutSheetPhoto` or a
 * `CutSheetOpenItem` — one schema, not the same idea written twice.
 *
 * 96, not 64: long enough for a per-car shot id, `cab.certificate:` (16) plus
 * a car id (`newCarId()`, `roomSetup.ts`, random). This bound used to be
 * declared separately at each of those call sites AND, on the upload route's
 * `PhotoMetaSchema`, at the OLD 64 — the same disagreement this file's own
 * `PhotoQuestionIdSchema` comment already warns about, and for the same
 * reason: two boundaries describing one field are one bug waiting for a
 * Tuesday. `tests/unit/fls-section.test.ts` pins the longest generated id
 * under this schema directly.
 */
export const ShotListIdSchema = z.string().min(1).max(96);

export const ScopeSchema = z.object({
  roomId: Id.optional(),
  groupId: Id.optional(),
  hoistwayId: Id.optional(),
});

/* ═══════════════════════════ answer values ═══════════════════════════ */

export const ContactSchema = z.object({
  name: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().optional(),
});

/**
 * A span of landings as one row — "2–20 sheetrock" instead of nineteen taps
 * (Q11). `wallType` is optional because Amendment 5's FLS landing spans (F11a,
 * F12a) are about which landings, not about a material; see `WallRange` in
 * types.ts. An L4a row always carries one — the material is that question's
 * whole answer — but that is a question-level fact, not a document invariant,
 * and this file does not hold §3/§4 business rules.
 */
export const WallRangeSchema = z
  .object({
    fromLanding: z.number().int().positive(),
    toLanding: z.number().int().positive(),
    wallType: WallTypeSchema.optional(),
  })
  .refine((r) => r.fromLanding <= r.toLanding, {
    message: 'fromLanding must not exceed toLanding',
    path: ['toLanding'],
  });

/**
 * The "Y/N + count" shape. types.ts states the invariant plainly — `flag:false`
 * always implies count 0 — so it is enforced here rather than left as a comment
 * nobody honours. Consequence for the form layer: zero the count when the
 * surveyor toggles the flag off, don't just hide the stepper.
 */
export const FlagCountSchema = z
  .object({
    flag: z.boolean(),
    count: Count,
  })
  .refine((fc) => fc.flag || fc.count === 0, {
    message: 'count must be 0 when flag is false',
    path: ['count'],
  });

export const MeasureSchema = z.object({
  feet: z.number().nonnegative().finite().optional(),
  lengthFt: z.number().nonnegative().finite().optional(),
  widthFt: z.number().nonnegative().finite().optional(),
});

export const AnswerValueSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('text'), value: z.string() }),
  z.object({ kind: z.literal('number'), value: z.number().finite() }),
  z.object({ kind: z.literal('bool'), value: z.boolean() }),
  z.object({ kind: z.literal('tristate'), value: z.enum(['yes', 'no', 'unknown']) }),
  z.object({ kind: z.literal('select'), value: z.string() }),
  z.object({ kind: z.literal('multiselect'), value: z.array(z.string()) }),
  z.object({ kind: z.literal('contact'), value: ContactSchema }),
  z.object({ kind: z.literal('date'), value: CalendarDate }),
  z.object({ kind: z.literal('measure'), value: MeasureSchema }),
  z.object({ kind: z.literal('ranges'), value: z.array(WallRangeSchema) }),
  z.object({ kind: z.literal('flagCount'), value: FlagCountSchema }),
]);

/* ═══════════════════════════ entity structure ═══════════════════════════ */

export const CarRefSchema = z.object({
  id: Id,
  label: z.string().min(1),
  onAts: z.boolean(),
});

export const GroupSchema = z.object({
  id: Id,
  name: z.string().min(1),
  carIds: z.array(Id),
  /** Landings ≈ floors (Q5). A group serves at least one. */
  landings: z.number().int().positive(),
});

export const HoistwaySchema = z.object({
  id: Id,
  name: z.string().min(1),
  carIds: z.array(Id),
});

export const MachineRoomSchema = z.object({
  id: Id,
  name: z.string().min(1),
  type: RoomTypeSchema,
  cars: z.array(CarRefSchema),
  groups: z.array(GroupSchema),
  hoistways: z.array(HoistwaySchema),
});

export const BuildingInfoSchema = z.object({
  id: Id,
});

/* ═══════════════════════════ ad hoc items (§2.5) ═══════════════════════ */

export const AdHocOtherSchema = z.object({
  id: Id,
  description: z.string().min(1),
});

export const AdHocItemsSchema = z.object({
  storageRental: z.boolean(),
  dumpsterRental: z.boolean(),
  cleanPitOfOilAndDebris: FlagCountSchema,
  parking: z.boolean(),
  other: z.array(AdHocOtherSchema),
});

/* ════════════════════════ notes / photos / gaps ════════════════════════ */

export const NoteSchema = z
  .object({
    id: Id,
    sectionInstanceId: SectionInstanceIdSchema,
    questionId: z.string().min(1).optional(),
    /** Amendment 8, D-041 — the shot this note is the written reason for. */
    shotListId: ShotListIdSchema.optional(),
    text: z.string(),
    createdAt: Timestamp,
  })
  .refine((note) => !(note.questionId && note.shotListId), {
    message: 'a note names a question or a shot, not both',
  });

export const PhotoRefSchema = z.object({
  id: Id,
  surveyId: Id,
  sectionInstanceId: SectionInstanceIdSchema,
  /**
   * Shared with `PhotoMetaSchema` on the upload route — deliberately the SAME
   * schema object, not the same idea written twice. See PhotoQuestionIdSchema.
   */
  questionId: PhotoQuestionIdSchema.optional(),
  /** Shared with `PhotoMetaSchema` on the upload route — see `ShotListIdSchema`. */
  shotListId: ShotListIdSchema.optional(),
  /** 2.4.0, D-038 — see the doc comment on `PhotoRef.label` in types.ts. */
  label: z.string().max(80).optional(),
  r2Key: z.string().min(1),
  contentType: z.string().min(1),
  bytes: Count,
  capturedAt: Timestamp,
  uploadState: PhotoUploadStateSchema,
  attempts: Count,
});

/**
 * D-017. `note` is deliberately NOT `.min(1)`: a surveyor holding a flashlight
 * in the other hand can mark something unavailable without typing, exactly as
 * `buildFlagGap` accepts a flag with no note. The acknowledgement is the act;
 * the words are extra.
 */
export const GapAcknowledgementSchema = z.object({
  gapId: Id,
  sectionInstanceId: SectionInstanceIdSchema,
  questionId: z.string().min(1).optional(),
  note: z.string(),
  acknowledgedAt: Timestamp,
});

export const GapSchema = z.object({
  id: Id,
  sectionInstanceId: SectionInstanceIdSchema,
  sectionTitle: z.string().min(1),
  questionId: z.string().min(1).optional(),
  reason: GapReasonSchema,
  message: z.string().min(1),
  surveyorNote: z.string().optional(),
});

/* ═══════════════════════ calculation & validation ═══════════════════════ */

export const DerivedLineSchema = z.object({
  questionId: z.string().min(1),
  scope: ScopeSchema,
  quantity: z.number().nonnegative().finite(),
  uom: z.string().min(1),
  basis: z.string().min(1),
  pricing: PricingModeSchema,
});

export const AutoFixSetEntrySchema = z.object({
  questionId: z.string().min(1),
  value: AnswerValueSchema,
});

export const AutoFixSchema = z.object({
  kind: z.literal('shunt_refurb_to_replace'),
  scope: ScopeSchema,
  set: z.array(AutoFixSetEntrySchema),
  explanation: z.string().min(1),
});

/**
 * The arithmetic behind a counting rule (§4 rules 1, 2, 3, 4, 6).
 *
 * Part of the contract rather than a screen-only convenience: `SubmittedSurvey`
 * carries `validation`, and unknown keys are STRIPPED here (see the module
 * header). Without this the running total and the target would be quietly
 * removed from the office's own record on the way through — the numbers Chip and
 * Tim need to read a gap at a glance, dropped by the schema that is supposed to
 * be preserving the record.
 */
export const IssueTallySchema = z.object({
  subject: z.string().min(1),
  running: z.number().int().nonnegative().finite(),
  target: z.number().int().nonnegative().finite(),
  unit: z.string().min(1),
  shape: z.enum(['exact', 'ceiling']),
});

export const ValidationIssueSchema = z.object({
  /** Specification §4 rule number, e.g. "V2". */
  ruleId: z.string().min(1),
  severity: ValidationSeveritySchema,
  scope: ScopeSchema,
  questionIds: z.array(z.string().min(1)),
  message: z.string().min(1),
  explanation: z.string().optional(),
  autoFix: AutoFixSchema.optional(),
  tally: IssueTallySchema.optional(),
});

/* ═══════════════════════════ survey document ═══════════════════════════ */

export const MissingPhotoSchema = z.object({
  sectionInstanceId: SectionInstanceIdSchema,
  sectionTitle: z.string().min(1),
  shotLabel: z.string().min(1),
  /** A FLAG only (Amendment 8, D-041) — copied from `MissingShot.required`. */
  required: z.literal(true).optional(),
});

/* ═══════════════════════════ the FLS cut sheet ═══════════════════════════ */

/**
 * `SubmittedSurvey.cutSheets.fls` — the fire alarm company's sheet, D-027.
 * Shape and meaning are documented on the types in src/core/flsCutSheet.ts;
 * this is the same shape as a schema so the composed document the office's
 * report agent reads is checked end to end, grid and all. `null` is a real
 * value throughout: it is how the sheet says "not on the record" without
 * inventing a No.
 */
export const PresenceSchema = z.enum(['yes', 'no', 'some', 'unknown']);

export const CutSheetPhotoSchema = z.object({
  id: Id,
  r2Key: z.string().min(1),
  contentType: z.string().min(1),
  bytes: Count,
  capturedAt: Timestamp,
  uploadState: PhotoUploadStateSchema,
  sectionInstanceId: SectionInstanceIdSchema,
  sectionTitle: z.string().min(1),
  questionId: PhotoQuestionIdSchema.optional(),
  /** See `ShotListIdSchema`. */
  shotListId: ShotListIdSchema.optional(),
  label: z.string().min(1),
  /** The catalog's own prompt, alongside `label` (round 8 task 5, D-038). */
  prompt: z.string().min(1).optional(),
  /** Amendment 8 (D-041) — which car a per-car shot belongs to, and its label. */
  carId: Id.optional(),
  carLabel: z.string().min(1).optional(),
  /** Amendment 8 (D-041) — copied from the section's shot list. */
  required: z.literal(true).optional(),
});

export const CutSheetDevicesSchema = z.object({
  smoke: PresenceSchema,
  heat: PresenceSchema,
  sprinkled: PresenceSchema,
});

export const LandingSpanSchema = z.object({
  fromLanding: z.number().int().nonnegative(),
  toLanding: z.number().int().nonnegative(),
});

export const CutSheetRoomSchema = z.object({
  id: Id,
  name: z.string(),
  type: RoomTypeSchema,
  cars: z.array(z.string()),
  sprinklered: z.boolean().nullable(),
  heatDetectorAtSprinklerHead: z.boolean().nullable(),
  shuntRelay: z.boolean().nullable(),
  relays: z
    .object({
      primary: z.boolean(),
      alternate: z.boolean(),
      flashingHat: z.boolean(),
      shunt: z.boolean(),
    })
    .nullable(),
  devices: z.object({ smoke: z.boolean(), heat: z.boolean() }).nullable(),
  location: z.string().nullable(),
  facpDistanceFt: z.number().nullable(),
  recallInstalled: z.enum(['yes', 'no', 'unknown']).nullable(),
  shuntDisconnectLocation: z.string().nullable(),
  mainDisconnectFeed: z.string().nullable(),
  photos: z.array(CutSheetPhotoSchema),
  carPhotos: z.array(
    z.object({ carId: Id, carLabel: z.string().min(1), photos: z.array(CutSheetPhotoSchema) }),
  ),
});

export const CutSheetHoistwaySchema = z.object({
  id: Id,
  name: z.string(),
  roomId: Id,
  roomName: z.string(),
  cars: z.array(z.string()),
  overhead: z.object({
    sprinklered: z.boolean().nullable(),
    devices: z.object({ smoke: z.boolean(), heat: z.boolean() }).nullable(),
  }),
  pit: z.object({
    devices: z
      .object({ sprinkler: z.boolean(), heat: z.boolean(), smoke: z.boolean() })
      .nullable(),
  }),
  photos: z.array(CutSheetPhotoSchema),
});

export const CutSheetLobbyGroupSchema = z.object({
  id: Id,
  name: z.string(),
  roomId: Id,
  roomName: z.string(),
  cars: z.array(z.string()),
  landings: z.number().int().nonnegative(),
  smokeDetectorsMissing: z.boolean().nullable(),
  landingsMissingSmoke: z.array(LandingSpanSchema),
  sprinklered: z.boolean().nullable(),
  sprinkleredLandings: z.array(LandingSpanSchema),
  sprinkleredLobbiesHaveHeat: z.boolean().nullable(),
  detectorTypes: z.object({ smoke: z.boolean(), heat: z.boolean(), other: z.boolean() }).nullable(),
  photos: z.array(CutSheetPhotoSchema),
});

export const CutSheetNoteSchema = z.object({
  sectionTitle: z.string().min(1),
  questionId: z.string().min(1).optional(),
  text: z.string(),
  createdAt: Timestamp,
});

/**
 * A blank on the sheet: a question nobody answered, OR (Amendment 8, D-041) a
 * required shot nobody took — never both, never neither. No `.shape` caller
 * exists on this schema, so `.refine` is safe here (the fallback, when one
 * does, is `.superRefine` on the array instead).
 */
export const CutSheetOpenItemSchema = z
  .object({
    questionId: z.string().min(1).optional(),
    /** See `ShotListIdSchema`. */
    shotListId: ShotListIdSchema.optional(),
    label: z.string().min(1),
    sectionInstanceId: SectionInstanceIdSchema,
    sectionTitle: z.string().min(1),
    reason: z.string().min(1).optional(),
  })
  .refine((item) => (item.questionId !== undefined) !== (item.shotListId !== undefined), {
    message: 'exactly one of questionId or shotListId',
  });

/**
 * `FlsCutSheet.vendorForm` — the fire alarm company's own three-page site
 * survey, field for field (spec §5.2, D-041, Task 7). Every member here
 * mirrors a member already checked above; this schema exists so a reader of
 * `vendorForm` alone (the office's PDF filler) gets the same guarantees.
 */
export const VendorFormSchema = z.object({
  template: z.literal('eft-site-survey'),
  project: z.object({
    name: z.string().nullable(),
    address: z.string().nullable(),
    surveyDate: Timestamp,
    surveyor: z.string().nullable(),
  }),
  fireAlarm: z.object({
    panelMakeModel: z.string().nullable(),
    distanceToMachineRoomFt: z.array(
      z.object({ roomId: Id, roomName: z.string(), feet: z.number().nullable() }),
    ),
  }),
  elevator: z.object({
    stops: z.array(
      z.object({ groupId: Id, groupName: z.string(), landings: z.number().int().nonnegative() }),
    ),
    machineRoomLocation: z.array(
      z.object({ roomId: Id, roomName: z.string(), location: z.string().nullable() }),
    ),
    controllerPhotos: z.array(CutSheetPhotoSchema),
    lobbyDetectionType: z.object({ smoke: PresenceSchema, heat: PresenceSchema, other: PresenceSchema }),
    lobbyDetectorNotes: z.array(CutSheetNoteSchema),
  }),
  photos: z.object({
    facp: z.array(CutSheetPhotoSchema),
    lobbyDetectors: z.array(CutSheetPhotoSchema),
    controllerExterior: z.array(CutSheetPhotoSchema),
    controllerInside: z.array(CutSheetPhotoSchema),
    pit: z.array(CutSheetPhotoSchema),
    hoistway: z.array(CutSheetPhotoSchema),
    cop: z.array(CutSheetPhotoSchema),
    mainDisconnect: z.array(CutSheetPhotoSchema),
    machineRoom: z.array(CutSheetPhotoSchema),
  }),
  accessNotes: z.string().nullable(),
  conditions: z.object({
    pitDevices: PresenceSchema,
    pitNotes: z.array(CutSheetNoteSchema),
    hoistwayDevices: PresenceSchema,
    hoistwayNotes: z.array(CutSheetNoteSchema),
    sprinklerSystem: z.enum(['yes', 'no', 'unknown']).nullable(),
    shuntRequired: PresenceSchema,
    shuntExisting: PresenceSchema,
    shuntLocation: z.array(z.object({ roomName: z.string(), text: z.string() })),
    mainDisconnectNotes: z.array(z.object({ roomName: z.string(), text: z.string() })),
    fieldNotes: z.array(CutSheetNoteSchema),
  }),
  recall: z.array(
    z.object({ roomId: Id, roomName: z.string(), installed: z.enum(['yes', 'no', 'unknown']).nullable() }),
  ),
});

export const FlsCutSheetSchema = z.object({
  template: z.literal('fls-turnkey-rfq'),
  /** 3 since round 9 (Amendment 8, D-041) — see FLS_CUT_SHEET_VERSION. */
  version: z.literal(3),
  job: z.object({
    name: z.string().nullable(),
    /** `submittedByName`, else `assignedTo` (D-041). */
    surveyor: z.string().nullable(),
    /** Composed from B2 + B2a–c (D-039); the parts travel beside it, null when the record has none. */
    address: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    zip: z.string().nullable(),
    buildingType: z.string().nullable(),
    date: Timestamp,
    elevators: z.number().nullable(),
    landings: z.number().int().nonnegative(),
    machineRooms: z.number().int().nonnegative(),
    hoistways: z.number().int().nonnegative(),
    groups: z.number().int().nonnegative(),
  }),
  panel: z.object({
    makeModel: z.string().nullable(),
    serviceProvider: z.string().nullable(),
    serviceContact: ContactSchema.nullable(),
    backBoxes: z.number().nullable(),
    notes: z.string().nullable(),
    /** F19 (D-041) — how the fire alarm company gets into the building. Retired from the catalog by D-045; older records still fill it, newer ones read null. */
    access: z.string().nullable(),
    photos: z.array(CutSheetPhotoSchema),
  }),
  summary: z.object({
    /** F18 (D-041) — building-level; a per-room/per-shaft Yes cannot stand in for it. */
    sprinklerSystem: z.enum(['yes', 'no', 'unknown']).nullable(),
    lobbies: CutSheetDevicesSchema,
    pit: CutSheetDevicesSchema,
    overhead: CutSheetDevicesSchema,
    machineRoom: CutSheetDevicesSchema,
    relays: z.object({
      primary: PresenceSchema,
      alternate: PresenceSchema,
      flashingHat: PresenceSchema,
      shunt: PresenceSchema,
    }),
    /** Required (M4) vs. existing (F7/F8) — D-041. */
    shunt: z.object({ required: PresenceSchema, existing: PresenceSchema }),
  }),
  machineRooms: z.array(CutSheetRoomSchema),
  hoistways: z.array(CutSheetHoistwaySchema),
  lobbyGroups: z.array(CutSheetLobbyGroupSchema),
  photos: z.array(CutSheetPhotoSchema),
  notes: z.array(CutSheetNoteSchema),
  open: z.array(CutSheetOpenItemSchema),
  vendorForm: VendorFormSchema,
});

export const CutSheetsSchema = z.object({ fls: FlsCutSheetSchema });

const surveyDocumentShape = {
  schemaVersion: z.string().regex(SEMVER_RE, 'expected a semver string, e.g. "2.0.0"'),
  surveyId: Id,
  origin: SurveyOriginSchema,
  status: SurveyStatusSchema,
  assignedTo: z.string().min(1).optional(),
  createdBy: z.string().min(1).optional(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
  submittedAt: Timestamp.optional(),
  /** 2.4.0, D-038 — see the doc comment on `SurveyDocument.submittedByName`. */
  submittedByName: z.string().min(1).max(120).optional(),
  building: BuildingInfoSchema,
  rooms: z.array(MachineRoomSchema),
  adHoc: AdHocItemsSchema,
  answers: z.record(AnswerKeySchema, AnswerValueSchema),
  /**
   * D-016 provenance: which of those answers the FORM wrote, not the surveyor.
   * Optional because every document written before D-016 has none, and because
   * a survey in which the surveyor answered everything themselves carries none
   * either. Validated as answer keys so a stray id cannot ride along.
   */
  defaultedAnswers: z.array(AnswerKeySchema).optional(),
  /**
   * D-017: the outstanding answers the surveyor deliberately marked unavailable
   * at the last look. Optional for the same two reasons `defaultedAnswers` is.
   */
  acknowledgedGaps: z.array(GapAcknowledgementSchema).optional(),
  notes: z.array(NoteSchema),
  photos: z.array(PhotoRefSchema),
  gaps: z.array(GapSchema),
};

/**
 * Object form, kept separate so `SubmittedSurvey` can `.extend()` it — a
 * `.superRefine()` yields a ZodEffects, which has no `.extend`.
 * Prefer `SurveyDocumentSchema` everywhere except when deriving a new shape.
 */
export const SurveyDocumentBaseSchema = z.object(surveyDocumentShape);

export const SubmittedSurveyBaseSchema = SurveyDocumentBaseSchema.extend({
  derived: z.array(DerivedLineSchema),
  validation: z.array(ValidationIssueSchema),
  missingPhotos: z.array(MissingPhotoSchema),
  cutSheets: CutSheetsSchema,
});

function reportDuplicates(
  ids: readonly string[],
  path: (string | number)[],
  label: string,
  ctx: z.RefinementCtx,
): void {
  const seen = new Set<string>();
  ids.forEach((id, i) => {
    if (seen.has(id)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...path, i, 'id'],
        message: `duplicate ${label} id "${id}"`,
      });
    }
    seen.add(id);
  });
}

/**
 * Document-level integrity: things that would silently corrupt the document's
 * own addressing scheme. Duplicate entity ids collapse distinct answer keys
 * onto each other; a stray photo `surveyId` orphans an R2 object. Business
 * rules from specification §4 are deliberately NOT here.
 */
function checkDocumentIntegrity(doc: SurveyDocument, ctx: z.RefinementCtx): void {
  if (!isSupportedSchemaVersion(doc.schemaVersion)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['schemaVersion'],
      message: versionMessage(schemaVersionVerdict(doc.schemaVersion), doc.schemaVersion),
    });
  }

  reportDuplicates(
    doc.rooms.map((r) => r.id),
    ['rooms'],
    'machine room',
    ctx,
  );

  doc.rooms.forEach((room, i) => {
    reportDuplicates(room.cars.map((c) => c.id), ['rooms', i, 'cars'], 'car', ctx);
    reportDuplicates(room.groups.map((g) => g.id), ['rooms', i, 'groups'], 'group', ctx);
    reportDuplicates(room.hoistways.map((h) => h.id), ['rooms', i, 'hoistways'], 'hoistway', ctx);
  });

  doc.photos.forEach((photo, i) => {
    if (photo.surveyId !== doc.surveyId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['photos', i, 'surveyId'],
        message:
          `photo "${photo.id}" is tagged to survey "${photo.surveyId}" but lives in ` +
          `document "${doc.surveyId}"`,
      });
    }
  });
}

/** The public contract: an in-flight or submitted survey document. */
export const SurveyDocumentSchema = SurveyDocumentBaseSchema.superRefine(checkDocumentIntegrity);

/** The public contract the estimate engine consumes: submitted + denormalized. */
export const SubmittedSurveySchema = SubmittedSurveyBaseSchema.superRefine(checkDocumentIntegrity);

/* ═════════════════════════════ notifications ═════════════════════════════ */

export const NotificationPayloadSchema = z.object({
  event: NotificationEventSchema,
  surveyId: Id,
  buildingName: z.string(),
  /** The composed line (D-039); the parts beside it only when the record has them. */
  address: z.string(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  surveyor: z.string(),
  createdAt: Timestamp,
  status: SurveyStatusSchema.optional(),
  roomCount: Count.optional(),
  unitCount: Count.optional(),
  gaps: z.array(GapSchema).optional(),
  missingPhotoCount: Count.optional(),
});

/* ═══════════════════════════════ parsing ═══════════════════════════════ */

export type SafeParseResult<T> =
  | { success: true; data: T }
  | { success: false; error: SchemaVersionError | z.ZodError };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Parse and version-gate a survey document. The version check runs first and
 * on purpose: a document from a future major must fail with one legible
 * sentence, not a wall of field errors describing fields we never had.
 */
export function parseSurvey(input: unknown): SurveyDocument {
  if (isRecord(input)) assertSchemaVersion(input);
  return SurveyDocumentSchema.parse(input);
}

export function safeParseSurvey(input: unknown): SafeParseResult<SurveyDocument> {
  if (isRecord(input)) {
    const verdict = schemaVersionVerdict(input['schemaVersion']);
    if (!isSupportedSchemaVersion(input['schemaVersion'])) {
      return { success: false, error: new SchemaVersionError(verdict, input['schemaVersion']) };
    }
  }
  const result = SurveyDocumentSchema.safeParse(input);
  return result.success
    ? { success: true, data: result.data }
    : { success: false, error: result.error };
}

export function parseSubmittedSurvey(input: unknown): SubmittedSurvey {
  if (isRecord(input)) assertSchemaVersion(input);
  return SubmittedSurveySchema.parse(input);
}

export function safeParseSubmittedSurvey(input: unknown): SafeParseResult<SubmittedSurvey> {
  if (isRecord(input)) {
    const verdict = schemaVersionVerdict(input['schemaVersion']);
    if (!isSupportedSchemaVersion(input['schemaVersion'])) {
      return { success: false, error: new SchemaVersionError(verdict, input['schemaVersion']) };
    }
  }
  const result = SubmittedSurveySchema.safeParse(input);
  return result.success
    ? { success: true, data: result.data }
    : { success: false, error: result.error };
}

/* ══════════════════════════ publishable JSON Schema ══════════════════════ */

export type SchemaTarget = 'survey' | 'submitted' | 'notification';

interface TargetSpec {
  schema: z.ZodTypeAny;
  name: string;
  description: string;
}

const SCHEMA_TARGETS = {
  survey: {
    schema: SurveyDocumentSchema,
    name: 'SurveyDocument',
    description:
      'An UpSurv 2.0 survey document. Entity tree per specification §1; every non-structural ' +
      'answer is keyed by its §3 question ID via answerKey().',
  },
  submitted: {
    schema: SubmittedSurveySchema,
    name: 'SubmittedSurvey',
    description:
      'A submitted survey plus the denormalized derived quantities and validation issues the ' +
      'estimate engine consumes. Catalog line items key off DerivedLine.questionId.',
  },
  notification: {
    schema: NotificationPayloadSchema,
    name: 'NotificationPayload',
    description: 'Payload posted on field-initiated survey creation and on survey submission.',
  },
} satisfies Record<SchemaTarget, TargetSpec>;

export interface PublishedJsonSchema {
  $id: string;
  title: string;
  description: string;
  /** Mirrors SCHEMA_VERSION so a published file is self-identifying. */
  schemaVersion: string;
  [key: string]: unknown;
}

/**
 * Emit the contract as JSON Schema so it can be published alongside the
 * estimate engine. Versioned in the `$id` — a consumer pins the URN it was
 * built against and a major bump is visible in a diff.
 */
export function toJsonSchema(target: SchemaTarget = 'survey'): PublishedJsonSchema {
  const spec = SCHEMA_TARGETS[target];
  const json = zodToJsonSchema(spec.schema, {
    name: spec.name,
    $refStrategy: 'root',
    target: 'jsonSchema7',
  });
  return {
    $id: `urn:upsurv:schema:${SCHEMA_VERSION}:${target}`,
    title: spec.name,
    description: spec.description,
    schemaVersion: SCHEMA_VERSION,
    ...json,
  };
}

/* ═══════════════════════════ drift guards ═══════════════════════════ */

/**
 * `MutuallyAssignable<A, B>` holds only when A and B accept each other in both
 * directions. That fails on a field added, removed, retyped, or flipped between
 * required and optional — which is every way types.ts and schema.ts can drift.
 *
 * This is the ONLY thing keeping the two files in sync; nothing compares them
 * at runtime. If a line below stops compiling, do not weaken the check — one of
 * the two files is wrong.
 */
type MutuallyAssignable<A, B> = [A] extends [B] ? ([B] extends [A] ? true : false) : false;

/**
 * `Assert<false>` is a compile error. Both arguments below are concrete, so the
 * error lands on the exact line whose schema drifted — do not refactor this
 * into a generic helper, that defers the check and reports one useless error
 * for the whole table.
 */
type Assert<T extends true> = T;

export type SchemaMirrorsTypes = [
  // enumerations
  Assert<MutuallyAssignable<z.infer<typeof RoomTypeSchema>, RoomType>>,
  Assert<MutuallyAssignable<z.infer<typeof FormSetSchema>, FormSet>>,
  Assert<MutuallyAssignable<z.infer<typeof SurveyOriginSchema>, SurveyOrigin>>,
  Assert<MutuallyAssignable<z.infer<typeof SurveyStatusSchema>, SurveyStatus>>,
  Assert<MutuallyAssignable<z.infer<typeof PricingModeSchema>, PricingMode>>,
  Assert<MutuallyAssignable<z.infer<typeof EntityLevelSchema>, EntityLevel>>,
  Assert<MutuallyAssignable<z.infer<typeof SectionKindSchema>, SectionKind>>,
  Assert<MutuallyAssignable<z.infer<typeof SectionStatusSchema>, SectionStatus>>,
  Assert<MutuallyAssignable<z.infer<typeof WallTypeSchema>, WallType>>,
  Assert<MutuallyAssignable<z.infer<typeof PhotoUploadStateSchema>, PhotoUploadState>>,
  Assert<MutuallyAssignable<z.infer<typeof GapReasonSchema>, GapReason>>,
  Assert<MutuallyAssignable<z.infer<typeof ValidationSeveritySchema>, ValidationSeverity>>,
  Assert<MutuallyAssignable<z.infer<typeof NotificationEventSchema>, NotificationEvent>>,
  Assert<MutuallyAssignable<z.infer<typeof AnswerKindSchema>, AnswerKind>>,
  // answer values
  Assert<MutuallyAssignable<z.infer<typeof ContactSchema>, Contact>>,
  Assert<MutuallyAssignable<z.infer<typeof WallRangeSchema>, WallRange>>,
  Assert<MutuallyAssignable<z.infer<typeof FlagCountSchema>, FlagCount>>,
  Assert<MutuallyAssignable<z.infer<typeof MeasureSchema>, Measure>>,
  Assert<MutuallyAssignable<z.infer<typeof AnswerValueSchema>, AnswerValue>>,
  // entity structure
  Assert<MutuallyAssignable<z.infer<typeof ScopeSchema>, Scope>>,
  Assert<MutuallyAssignable<z.infer<typeof CarRefSchema>, CarRef>>,
  Assert<MutuallyAssignable<z.infer<typeof GroupSchema>, Group>>,
  Assert<MutuallyAssignable<z.infer<typeof HoistwaySchema>, Hoistway>>,
  Assert<MutuallyAssignable<z.infer<typeof MachineRoomSchema>, MachineRoom>>,
  Assert<MutuallyAssignable<z.infer<typeof BuildingInfoSchema>, BuildingInfo>>,
  // ad hoc, notes, photos, gaps
  Assert<MutuallyAssignable<z.infer<typeof AdHocOtherSchema>, AdHocOther>>,
  Assert<MutuallyAssignable<z.infer<typeof AdHocItemsSchema>, AdHocItems>>,
  Assert<MutuallyAssignable<z.infer<typeof NoteSchema>, Note>>,
  Assert<MutuallyAssignable<z.infer<typeof PhotoRefSchema>, PhotoRef>>,
  Assert<MutuallyAssignable<z.infer<typeof GapSchema>, Gap>>,
  Assert<MutuallyAssignable<z.infer<typeof GapAcknowledgementSchema>, GapAcknowledgement>>,
  // calculation & validation
  Assert<MutuallyAssignable<z.infer<typeof DerivedLineSchema>, DerivedLine>>,
  Assert<MutuallyAssignable<z.infer<typeof AutoFixSchema>, AutoFix>>,
  Assert<MutuallyAssignable<z.infer<typeof IssueTallySchema>, IssueTally>>,
  Assert<MutuallyAssignable<z.infer<typeof ValidationIssueSchema>, ValidationIssue>>,
  Assert<MutuallyAssignable<z.infer<typeof VendorFormSchema>, VendorForm>>,
  Assert<MutuallyAssignable<z.infer<typeof FlsCutSheetSchema>, FlsCutSheet>>,
  // public contracts
  Assert<MutuallyAssignable<z.infer<typeof SurveyDocumentSchema>, SurveyDocument>>,
  Assert<MutuallyAssignable<z.infer<typeof SubmittedSurveySchema>, SubmittedSurvey>>,
  Assert<MutuallyAssignable<z.infer<typeof NotificationPayloadSchema>, NotificationPayload>>,
];
